import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, ChevronDown, MapPin } from "lucide-react";
import { BRAND, PROVINCES, SERVICES, CITIES, SECTORS } from "@/data/site";
import { url } from "@/data/content";
import { SunMark } from "@/components/Sun";

const NavDrop = ({ label, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors"
        aria-expanded={open}
      >
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="absolute left-0 top-full pt-1 z-50">{children}</div>}
    </div>
  );
};

const Header = () => {
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setMobile(false);
  }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-[#0B0F17] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-9 text-xs">
          <span className="flex items-center gap-2 text-amber-500 font-mono uppercase tracking-widest">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> 24/7 Dispatch Active
          </span>
          <div className="flex items-center gap-4">
            <a href={BRAND.phoneHref} data-testid="topbar-phone-link" className="flex items-center gap-1.5 text-slate-300 hover:text-amber-400 transition-colors">
              <Phone className="h-3 w-3" /> <span className="hidden sm:inline">{BRAND.phone}</span><span className="sm:hidden">Call</span>
            </a>
            <a href={BRAND.emailHref} data-testid="topbar-email-link" className="hidden md:flex items-center gap-1.5 text-slate-300 hover:text-amber-400 transition-colors">
              <Mail className="h-3 w-3" /> {BRAND.email}
            </a>
          </div>
        </div>
      </div>
      <div className="backdrop-blur-xl bg-slate-900/85 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3 shrink-0">
            <SunMark size={38} />
            <span className="leading-tight">
              <span className="block font-display font-bold text-slate-50 text-sm sm:text-base tracking-tight">Indigenous Security Services</span>
              <span className="block text-[11px] font-semibold text-slate-300 tracking-wide">1st Nation Security</span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-amber-500">First Nation Owned & Operated</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            <NavDrop label="Services" testid="nav-services-dropdown">
              <div className="w-[34rem] rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl p-4 grid grid-cols-2 gap-1">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={url.service(s)} data-testid={`nav-service-${s.slug}`} className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-amber-500/10 hover:text-amber-400 transition-colors">
                    {s.name}
                  </Link>
                ))}
                <Link to={url.services()} className="col-span-2 mt-1 rounded-lg bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-400 hover:bg-amber-500/20 transition-colors">
                  View All 14 Security Services
                </Link>
              </div>
            </NavDrop>
            <NavDrop label="Locations" testid="nav-locations-dropdown">
              <div className="w-[30rem] rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl p-4 grid grid-cols-2 gap-1 max-h-[70vh] overflow-y-auto">
                {PROVINCES.map((p) => (
                  <Link key={p.slug} to={url.prov(p)} data-testid={`nav-province-${p.slug}`} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-amber-500/10 hover:text-amber-400 transition-colors">
                    <MapPin className="h-3.5 w-3.5 text-amber-500/70" /> {p.name}
                  </Link>
                ))}
                <Link to={url.locations()} className="col-span-2 mt-1 rounded-lg bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-400 hover:bg-amber-500/20 transition-colors">
                  All Provinces & 55+ Cities
                </Link>
              </div>
            </NavDrop>
            <Link to={url.sectors()} data-testid="nav-sectors-link" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors">Sectors</Link>
            <Link to={url.insights()} data-testid="nav-insights-link" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors">Insights</Link>
            <Link to={url.about()} data-testid="nav-about-link" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors">About</Link>
            <Link to="/contact/" data-testid="nav-contact-link" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors">Contact</Link>
            <Link to={url.sitemap()} data-testid="nav-sitemap-link" className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-amber-400 transition-colors">Sitemap</Link>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={BRAND.phoneHref}
              data-testid="nav-phone-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-slate-950 shadow-lg shadow-amber-500/25 transition-all hover:bg-amber-400 hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a
              href={BRAND.emailHref}
              data-testid="nav-email-button"
              aria-label={`Email ${BRAND.email}`}
              className="hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-600 text-slate-300 transition-all hover:border-amber-500/60 hover:text-amber-400"
            >
              <Mail className="h-4 w-4" />
            </a>
            <Link
              to="/fr/"
              data-testid="nav-fr-link"
              title="Version francaise"
              className="hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-600 text-slate-300 text-xs font-bold transition-all hover:border-amber-500/60 hover:text-amber-400"
            >
              FR
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setMobile((m) => !m)}
              aria-label="Toggle menu"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-700 text-slate-200"
            >
              {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobile && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#0F172A] max-h-[75vh] overflow-y-auto">
            <div className="px-4 py-4 space-y-4">
              <a href={BRAND.phoneHref} data-testid="mobile-phone-button" className="flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-bold text-slate-950">
                <Phone className="h-4 w-4" /> Call {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} data-testid="mobile-email-button" className="flex items-center justify-center gap-2 rounded-full border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-100">
                <Mail className="h-4 w-4" /> {BRAND.email}
              </a>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-amber-500 mb-2">Services</p>
                <div className="grid grid-cols-1 gap-0.5">
                  {SERVICES.map((s) => (
                    <Link key={s.slug} to={url.service(s)} className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5">{s.name}</Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-amber-500 mb-2">Provinces & Territories</p>
                <div className="grid grid-cols-2 gap-0.5">
                  {PROVINCES.map((p) => (
                    <Link key={p.slug} to={url.prov(p)} className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5">{p.name}</Link>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pb-4">
                <Link to={url.sectors()} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200">Sectors</Link>
                <Link to={url.insights()} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200">Insights</Link>
                <Link to={url.about()} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200">About</Link>
                <Link to="/contact/" className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200">Contact</Link>
                <Link to={url.sitemap()} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200">Sitemap</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const FooterCol = ({ title, links, testid }) => (
  <div>
    <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-amber-500 mb-4">{title}</h3>
    <ul className="space-y-2" data-testid={testid}>
      {links.map((l) => (
        <li key={l.path}>
          <Link to={l.path} className="text-sm text-slate-400 hover:text-amber-400 transition-colors">{l.label}</Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const topCities = CITIES.filter((c) => ["toronto", "montreal", "vancouver", "calgary", "edmonton", "ottawa", "winnipeg", "halifax", "quebec-city", "saskatoon", "thunder-bay", "victoria"].includes(c.slug));
  return (
    <footer className="border-t border-white/10 bg-[#0B0F17] mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <SunMark size={44} />
              <span className="leading-tight">
                <span className="block font-display font-bold text-slate-50">Indigenous Security Services</span>
                <span className="block text-[11px] font-semibold text-slate-400 tracking-wide">1st Nation Security</span>
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              {BRAND.tagline}. Licensed, insured and supervised guard services in every province and territory, 24/7/365.
            </p>
            <a href={BRAND.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 mb-2">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.emailHref} data-testid="footer-email-link" className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400">
              <Mail className="h-4 w-4" /> {BRAND.email}
            </a>
            <p className="mt-4 text-xs font-mono text-slate-500 uppercase tracking-widest">Hours: 24/7/365 Guard Dispatch</p>
          </div>
          <FooterCol testid="footer-services-links" title="Security Services" links={SERVICES.map((s) => ({ path: url.service(s), label: s.name }))} />
          <FooterCol testid="footer-provinces-links" title="Provinces & Territories" links={PROVINCES.map((p) => ({ path: url.prov(p), label: p.name }))} />
          <FooterCol testid="footer-cities-links" title="Top Cities" links={topCities.map((c) => ({ path: url.city(c), label: `Security Guards ${c.name}` }))} />
          <div className="space-y-8">
            <FooterCol testid="footer-sectors-links" title="Sectors" links={SECTORS.slice(0, 6).map((x) => ({ path: url.sector(x), label: x.name }))} />
            <FooterCol testid="footer-company-links" title="Company" links={[
              { path: url.about(), label: "About Us" },
              { path: "/contact/", label: "Contact" },
              { path: url.insights(), label: "Insights & Guides" },
              { path: url.locations(), label: "All Locations" },
              { path: url.sitemap(), label: "Sitemap" },
            ]} />
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">© 2026 {BRAND.name}. All rights reserved. Licensed security guard services across Canada.</p>
          <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">Proudly First Nation-Owned & Operated</p>
        </div>
      </div>
    </footer>
  );
};

const StickyContactBar = () => (
  <div data-testid="sticky-contact-bar" className="fixed bottom-3 inset-x-3 sm:bottom-5 z-50 flex justify-center pointer-events-none">
    <div className="pointer-events-auto flex items-center rounded-full border border-amber-500/40 bg-slate-900/90 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
      <span className="hidden md:flex items-center gap-2 pl-5 pr-3 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-500">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> 24/7 Dispatch
      </span>
      <a
        href={BRAND.phoneHref}
        data-testid="sticky-call-button"
        className="flex items-center gap-2 bg-amber-500 px-5 sm:px-6 py-3.5 text-sm font-bold text-slate-950 transition-colors hover:bg-amber-400"
      >
        <Phone className="h-4 w-4" /> Call {BRAND.phone}
      </a>
      <a
        href={BRAND.emailHref}
        data-testid="sticky-email-button"
        className="flex items-center gap-2 px-5 sm:px-6 py-3.5 text-sm font-semibold text-slate-100 transition-colors hover:text-amber-400 border-l border-white/10"
      >
        <Mail className="h-4 w-4" /> Email Us
      </a>
    </div>
  </div>
);

export const Layout = () => {
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200 font-sans antialiased">
      <Header />
      <section data-testid="ccib-member-band" className="border-b border-white/5 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <div className="rounded-xl bg-white px-6 py-3 shadow-lg shadow-black/30">
            <img
              src="/ccib-certified-indigenous-business.png"
              alt="A Proud Member of the Canadian Council for Indigenous Business — CCIB Certified Indigenous Business"
              className="h-14 sm:h-16 w-auto"
              loading="eager"
            />
          </div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-500/90 text-center sm:text-left">
            Certified Indigenous Business
          </p>
        </div>
      </section>
      <main>
        <Outlet />
      </main>
      <Footer />
      <div className="h-20" aria-hidden="true" />
      <StickyContactBar />
    </div>
  );
};
